import { Button, ButtonGroup, ConfirmDialog, Field, NumericInput, TextOutput } from 'Components/Core';
import { NamePrefixModifier, withInjectedProps } from 'Components/Core/InjectedPropsContext';
import { GridFieldSection, HorizontalGrid, ListFieldSection } from 'Components/Layout';
import { CreateWorksheetSelector, WorksheetEquipmentDropdown } from 'Components/Worksheets/Core';
import { toMessageProps } from 'Components/Worksheets/Core/Helpers/messageHelpers';
import { Message } from 'Data/ViewModels/IApiDataModel';
import { pipe } from 'Lib/Hooks/Selectors';
import deepEquals from 'Lib/Object/deepEquals';
import { uniqBy } from 'ramda';
import { useSelector } from 'react-redux';

interface ICompactionMouldProps {
  createWorksheetSelector?: CreateWorksheetSelector;
  onAddMouldClick: () => void;
  onRemoveMouldClick: () => void;
  name?: string;
  removeMouldVisible?: boolean;
  addMouldVisible?: boolean;
  disabledMouldOptions?: string[];
}

const CompactionMould: React.FC<ICompactionMouldProps> = ({
  createWorksheetSelector,
  onAddMouldClick,
  onRemoveMouldClick,
  name,
  removeMouldVisible,
  addMouldVisible,
  disabledMouldOptions,
}) => {
  const mouldDimensionsMessages = useSelector(
    pipe(
      createWorksheetSelector!(name + '.Messages'),
      (m) =>
        (m as Array<Message>)?.filter((m) =>
          [
            'DiameterReading1',
            'DiameterReading2',
            'DiameterReading3',
            'DiameterReading4',
            'DiameterReadingsAverage',
          ].includes(m.PropertyName)
        ),
      (m) => uniqBy((x) => x.message, m?.map(toMessageProps) ?? [])
    ),
    deepEquals
  );

  const readings = [
    [<Field input={<NumericInput />} name="DiameterReading1" />],
    [<Field input={<NumericInput />} name="DiameterReading2" />],
    [<Field input={<NumericInput />} name="DiameterReading3" />],
    [<Field input={<NumericInput />} name="DiameterReading4" />],
    [<Field input={<TextOutput />} name="DiameterReadingsAverage" />],
  ];

  const colHeaders = Array(5).fill('').fill('Average (mm)', 4);
  const rowHeaders = ['Diameter (mm)'];

  return (
    <NamePrefixModifier appendNamePrefix={name}>
      <ListFieldSection>
        <Field
          input={<WorksheetEquipmentDropdown />}
          equipmentType="density-mould"
          name="Mould"
          disabledOptions={disabledMouldOptions}
        />
        <Field input={<NumericInput />} name="Mass" />
      </ListFieldSection>
      <ListFieldSection wide>
        <GridFieldSection>
          <HorizontalGrid
            rowHeaders={rowHeaders}
            rowHeaderWidth={100}
            colWidths={124}
            colHeaders={colHeaders}
            data={readings}
            tabDirection="horizontal"
          />
        </GridFieldSection>
        <ListFieldSection messages={mouldDimensionsMessages} />
        <ListFieldSection single>
          <Field input={<NumericInput />} name="MouldAndCollarHeight" />
          <ButtonGroup>
            <ConfirmDialog confirmTitle="Removing Mould" confirmText="Are you sure you want to remove this Mould?">
              <Button onClick={onRemoveMouldClick} visible={removeMouldVisible ?? false} error>
                Remove Mould
              </Button>
            </ConfirmDialog>
            <Button onClick={onAddMouldClick} visible={addMouldVisible ?? false}>
              Add Mould
            </Button>
          </ButtonGroup>
        </ListFieldSection>
      </ListFieldSection>
    </NamePrefixModifier>
  );
};

const BsCompactionMould = withInjectedProps(CompactionMould);

export { BsCompactionMould };
export default BsCompactionMould;
